import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MatSort } from "@angular/material/sort";
import { MatTable, MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute } from "@angular/router";
import { AlertController, ModalController } from "@ionic/angular";
import { OidcSecurityService } from "angular-auth-oidc-client";
import { readEml } from "eml-parse-js";
import { ReadedEmlJson } from "eml-parse-js/dist/interface";
import { finalize, firstValueFrom, Observable, throwError } from "rxjs";
import { map } from "rxjs/operators";
import { OdataDokumentResponse } from "../../../api/models/odata-dokument-response";
import { OdataGutachtenDokumentPatch } from "../../../api/models/odata-gutachten-dokument-patch";
import { DokumenteService } from "../../../api/services/dokumente.service";
import { UserRole } from "../../enums/global-enums";
import { replaceFileExtensionConditionally } from "../../global-functions/global-functions";
import { AuthService } from "../../services/auth.service";
import { DownloadService } from "../../services/download.service";
import { LoveItToastService } from "../../services/love-it-toast.service";
import { EditContentModalComponent } from "../edit-content/edit-content-modal.component";
import { FileViewerComponent } from "../pdf-viewer/file-viewer.component";

export enum DokumentTyp {
  AktDokumente,
  GutachtenDokumente,
}

enum DocumentTableColumns {
  Sort = "sort",
  Preview = "preview",
  Kategorie = "kategorie",
  Bezeichnung = "bezeichnung",
  DateiName = "datei_name",
  SelectButton = "SelectButton",
  PreviewButtons = "PreviewButtons",
  DeleteButtons = "DeleteButtons",
  Art = "art",
  Datum = "datum",
  Adressattext = "adressattext",
  Quelle = "quelle",
  Bemerkung = "bemerkung",
}

@Component({
  selector: "app-file-overview",
  templateUrl: "./file-overview.component.html",
  styleUrls: ["./file-overview.component.scss"],
  standalone: false,
})
export class FileOverviewComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() dokumente: OdataDokumentResponse[];
  @Input() dokumentTyp: DokumentTyp;
  @Input() fetchFailed: boolean;
  @Input() hideFileNamesColumn: boolean;
  @Input() disableSortChanges: boolean;
  @Input() aktId: number;
  @Input() gutachtenId: number;
  @Output() onClickEmail: EventEmitter<ReadedEmlJson> = new EventEmitter<ReadedEmlJson>();

  displayedGutachtenColumns: string[] = [
    DocumentTableColumns.Sort,
    DocumentTableColumns.Preview,
    DocumentTableColumns.Kategorie,
    DocumentTableColumns.Bezeichnung,
    DocumentTableColumns.DateiName,
    DocumentTableColumns.PreviewButtons,
    DocumentTableColumns.DeleteButtons,
  ];
  displayedAktColumns: string[] = [
    DocumentTableColumns.Art,
    DocumentTableColumns.Datum,
    DocumentTableColumns.Adressattext,
    DocumentTableColumns.Kategorie,
    DocumentTableColumns.Quelle,
    DocumentTableColumns.Bemerkung,
    DocumentTableColumns.PreviewButtons,
  ];
  displayedAktColumnsAdmin: string[] = [
    DocumentTableColumns.Art,
    DocumentTableColumns.Datum,
    DocumentTableColumns.Adressattext,
    DocumentTableColumns.Kategorie,
    DocumentTableColumns.Quelle,
    DocumentTableColumns.Bemerkung,
    DocumentTableColumns.DateiName,
    DocumentTableColumns.PreviewButtons,
  ];
  currentColumns: string[] = [];
  @ViewChild("matTable", { static: true }) matTable: MatTable<any>;

  dataSource: MatTableDataSource<any>;

  @ViewChild(MatSort) sort: MatSort;
  sortWasChanged: boolean = false;
  uploadingSortChanges: boolean = false;
  disableDatatableSorting: boolean = false;
  downloadingDocument: boolean = false;
  downloadingDocumentId: number;

  defaultDokumente = [];
  notUploadedHint: string = "Die Datei wurde zwischengespeichert und ist noch nicht vollständig übertragen worden.";

  get getAktId(): number {
    return this.aktId || this.route.snapshot.queryParams.aktid;
  }

  constructor(
    private dokumenteService: DokumenteService,
    private oidcSecurityService: OidcSecurityService,
    private route: ActivatedRoute,
    private ngZone: NgZone,
    private alertController: AlertController,
    private modalController: ModalController,
    private loveItToastService: LoveItToastService,
    public downloadService: DownloadService,
    public authService: AuthService,
  ) {
    this.dataSource = new MatTableDataSource([]);
  }

  ngOnInit() {
    switch (this.dokumentTyp) {
      case DokumentTyp.AktDokumente:
        this.currentColumns = this.authService.hasCurrentUserAccessForRole(UserRole.Backoffice)
          ? this.displayedAktColumnsAdmin
          : this.displayedAktColumns;
        break;
      case DokumentTyp.GutachtenDokumente:
        this.currentColumns = this.displayedGutachtenColumns;
        this.disableDatatableSorting = true;
        break;
    }
    if (this.hideFileNamesColumn) {
      this.currentColumns = this.currentColumns.filter((column) => column !== DocumentTableColumns.DateiName);
    }
    if (this.onClickEmail.observed) {
      this.currentColumns.splice(this.currentColumns.length - 1, 0, DocumentTableColumns.SelectButton);
    }
    if (this.dokumente) {
      this.dataSource = new MatTableDataSource(this.dokumente);
      this.loadDokumente();
    }
  }

  ngAfterViewInit() {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.initSorting();
      });
    });
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.dokumente && !changes.dokumente.firstChange) {
      this.loadDokumente();
    }
  }

  loadDokumente() {
    this.dataSource.data = this.dokumente || [];
    this.dataSource.data.map((entry) => {
      entry.datei_name = replaceFileExtensionConditionally(entry.datei_name);
    });
    if (this.dokumentTyp == DokumentTyp.GutachtenDokumente) {
      this.dataSource.data.sort((a, b) => a.sort - b.sort);
    }
    if (this.dokumentTyp == DokumentTyp.AktDokumente) {
      this.dataSource.sortingDataAccessor = (item, property) => {
        if (property === "datum") {
          const timestamp = item?.datum ? new Date(item.datum).getTime() : 0;
          return [timestamp, item?.oid];
        }
        return item[property];
      };
    }
    this.defaultDokumente = Array.from(this.dokumente || []);
  }

  initSorting() {
    if (!this.sort) {
      return;
    }
    this.dataSource.sort = this.sort;
    if (this.dokumentTyp == DokumentTyp.GutachtenDokumente) {
      this.dataSource.sort.sort({
        id: "sort",
        start: "asc",
        disableClear: false,
      });
    }
    if (this.dokumentTyp == DokumentTyp.AktDokumente) {
      this.dataSource.sort.sort({
        id: "datum",
        start: "desc",
        disableClear: false,
      });
    }
  }
  drop(event: CdkDragDrop<string>) {
    if (this.disableSortChanges) {
      this.loveItToastService.showToastMessage("Die Sortierung darf derzeit nicht geändert werden.");
      return;
    }
    if (this.uploadingSortChanges) {
      this.loveItToastService.showToastMessage("Sortierung darf nicht während eines Uploads geändert werden.");
      return;
    }
    if (this.notUploadedFilesPresent()) {
      this.loveItToastService.showToastMessage(
        "Sortierung darf nicht geändert werden solange nicht alle Dateien vollständig übertragen wurden.",
      );
      return;
    }

    const previousIndex = this.dataSource.data.findIndex((d) => d === event.item.data);

    moveItemInArray(this.dataSource.data, previousIndex, event.currentIndex);
    this.updateDocumentsSortFields();
    this.dataSource._updateChangeSubscription();
  }

  updateDocumentsSortFields() {
    this.dataSource.data.forEach((document, index) => {
      if (document.sort != index + 1) {
        this.sortWasChanged = true;
        document.sort = index + 1;
      }
    });
  }

  resetLocalDocumentsSort() {
    this.dataSource.data = Array.from(this.defaultDokumente || []);
    this.updateDocumentsSortFields();
    this.dataSource._updateChangeSubscription();
    this.sortWasChanged = false;
  }

  async uploadSortingOfDocuments() {
    this.uploadingSortChanges = true;

    try {
      await firstValueFrom(
        this.dokumenteService.dokumenteOdataGutachtenSortallCreateResponse({
          akt_id: this.getAktId,
          token: this.oidcSecurityService.getToken(),
          dokumente: this.dataSource.data.map((document) => ({
            Oid: document.oid,
            Sort: document.sort,
          })),
          gutachten_id: this.gutachtenId,
        }),
      );
      this.sortWasChanged = false;
    } finally {
      this.uploadingSortChanges = false;
    }
  }

  async selectElement(fileData: OdataDokumentResponse) {
    this.downloadFile(fileData).subscribe({
      next: async (result) => {
        const html = atob(result.base64);
        const parsedEmlJson = readEml(html) as ReadedEmlJson;
        this.onClickEmail.emit(parsedEmlJson);
      },
      error: (err) => {
        this.downloadingDocument = false;
      },
    });
  }

  async openDocumentViewerModal(fileData: OdataDokumentResponse) {
    this.downloadFile(fileData).subscribe({
      next: async (result) => {
        const modal = await this.modalController.create({
          component: FileViewerComponent,
          cssClass: "modal-fullscreen",
          backdropDismiss: true,
          componentProps: {
            fileSource: result.base64,
            fileName: fileData.datei_name,
            edit: false,
          },
        });
        await modal.present();
      },
      error: (err) => {
        this.downloadingDocument = false;
      },
    });
  }

  downloadFile(fileData: OdataDokumentResponse): Observable<{ base64: string }> {
    this.downloadingDocument = true;
    this.downloadingDocumentId = fileData?.oid;

    const token = this.oidcSecurityService.getToken();

    const requestObservable =
      this.dokumentTyp === DokumentTyp.AktDokumente
        ? this.dokumenteService.dokumenteOdataAktDownloadCreateResponse({
            dokument_id: fileData.oid,
            akt_id: this.getAktId,
            token,
          })
        : this.dokumentTyp === DokumentTyp.GutachtenDokumente
          ? this.dokumenteService.dokumenteOdataGutachtenDownloadCreateResponse({
              dokument_id: fileData.oid,
              akt_id: this.getAktId,
              token,
            })
          : throwError(() => new Error("Fehler beim Laden: Typ des Dokuments unbekannt."));

    return requestObservable.pipe(
      map((response) => ({ base64: response.body?.toString() ?? "" })),
      finalize(() => {
        this.downloadingDocument = false;
        this.downloadingDocumentId = null;
      }),
    );
  }

  async deleteGutachtenDokumentButton(file) {
    const alert = await this.alertController.create({
      header: `Die Datei "${this.downloadService.getFileNameFromPath(file.datei_name)}" löschen?`,
      message: "Gelöschte Dateien können nicht wiederhergestellt werden!",
      buttons: [
        {
          text: "Abbrechen",
          role: "cancel",
          cssClass: "color-primary",
        },
        {
          cssClass: "color-danger",
          text: "Löschen",
          handler: () => {
            this.deleteGutachtenDokument(file.oid);
          },
        },
      ],
    });
    document.body.appendChild(alert);
    await alert.present();
  }

  deleteGutachtenDokument(fileId: number) {
    this.dokumenteService
      .dokumenteOdataGutachtenDeleteCreateResponse({
        dokument_id: fileId,
        akt_id: this.getAktId,
        token: this.oidcSecurityService.getToken(),
      })
      .subscribe({
        next: () => {
          this.dokumente = this.dokumente.filter((entry) => entry.oid !== fileId);
          this.defaultDokumente = this.defaultDokumente.filter((entry) => entry.oid !== fileId);
          this.dataSource.data = this.dokumente || [];
        },
        error: () => {},
      });
  }

  async openEditModalForDokumentButton<
    key extends keyof OdataDokumentResponse,
    patchKey extends keyof OdataGutachtenDokumentPatch,
  >(dokument: OdataDokumentResponse, dokumentKey: key, patchDokumentKey: patchKey, title: string) {
    const propertyValue = dokument[dokumentKey];
    const modal = await this.modalController.create({
      component: EditContentModalComponent,
      cssClass: "one-line-modal",
      backdropDismiss: false,
      componentProps: {
        title: title,
        value: propertyValue,
        saveFunction: async (newValue: string) => {
          const patchData: OdataGutachtenDokumentPatch = {
            akt_id: this.getAktId,
            token: this.oidcSecurityService.getToken(),
            Oid: dokument.oid,
          } as OdataGutachtenDokumentPatch;

          patchData[patchDokumentKey as string] = newValue;

          await firstValueFrom(this.dokumenteService.dokumenteOdataGutachtenUpdateCreate(patchData));
          return newValue;
        },
      },
    });
    await modal.present();

    const { data, role } = await modal.onDidDismiss<OdataDokumentResponse[key]>();
    if (role === "confirm") {
      dokument[dokumentKey] = data;
    }
  }

  notUploadedFilesPresent(): boolean {
    return this.dataSource.data.some((entry) => entry.not_uploaded);
  }

  protected readonly DokumentTyp = DokumentTyp;
  protected readonly UserRole = UserRole;
  protected readonly DocumentTableColumns = DocumentTableColumns;
}
